import React from "react";
import { PhoneOutlinedIcon, WhatsAppIcon } from "../components/icons";
import toPriceDisplay from "../utils/toPriceDisplay";
import phoneDisplay from "../utils/phoneDisplay";
import documentDisplay from "../utils/documentDisplay";
import documentLabelDisplay from "../utils/documentLabelDisplay";
import CheckoutSection from "../components/CheckoutSection";
import { WHATSAPP_CHAT_URL } from "../ducks/consts";

/**
 * Detalhes do pedido na tela de status: itens, valores, para onde vai e o
 * contato da loja. Cada bloco é uma seção em cartão, como no checkout.
 * Também aparece no status do QR Code.
 */
export default function OrderDetailsStatus({
  shortReference,
  shop,
  cache,
  order,
  isOrderVisibleInitialValue,
}) {
  const [isOrderVisible, setOrderVisible] = React.useState(
    isOrderVisibleInitialValue,
  );

  const checkout = cache ? cache.checkout : null;
  const deliveryAddress = cache ? checkout.deliveryAddress : null;
  const costs = cache ? cache.costs : null;
  const mode = order.toGo
    ? "para retirar"
    : order.toDelivery
      ? "para entrega"
      : `na loja`;
  const inLocoTableText = Number(order.tableId)
    ? `${shop.inLocoTitle} ${order.tableId}`
    : order.tableId;
  const inLocoText =
    checkout && checkout.inLocoLocation
      ? `${checkout.inLocoLocation} ${inLocoTableText}`
      : inLocoTableText;
  const message = `Referente ao pedido *${mode}* nº *${shortReference}*`;
  const whatsappDigits = shop ? (shop.whatsapp || "").replace(/\D/g, "") : "";
  const isSameShop = shop && shop.id === order.shopId;

  return (
    <>
      {cache && (
        <CheckoutSection
          title="Seu pedido"
          action={
            <button
              type="button"
              className="link-button"
              onClick={() => setOrderVisible(!isOrderVisible)}
              aria-expanded={isOrderVisible}
            >
              {isOrderVisible ? "Ocultar" : "Ver detalhes"}
            </button>
          }
        >
          {isOrderVisible ? (
            <>
              <ul className="cart-list__items">
                {cache.orderedProducts.map((product, productIndex) => (
                  <li key={`cached-product-${cache.orderId}-${productIndex}`}>
                    <div className="cart-item">
                      <div className="cart-item__body">
                        <div className="cart-item__row">
                          <p className="cart-item__name">{product.name}</p>
                          <span className="cart-item__row-price">
                            {toPriceDisplay(product.price)}
                          </span>
                        </div>
                        {product.detailsList &&
                          product.detailsList.length > 0 && (
                            <ul className="cart-item__details">
                              {product.detailsList.map((detail, index) => (
                                <li key={index}>{detail}</li>
                              ))}
                            </ul>
                          )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <OrderCosts checkout={checkout} costs={costs} />
            </>
          ) : (
            <p className="status-card-text status-card-body">
              {`${cache.orderedProducts.length} ${
                cache.orderedProducts.length === 1 ? "item" : "itens"
              } · ${toPriceDisplay(costs.total)}`}
            </p>
          )}
        </CheckoutSection>
      )}

      {cache && (
        <CheckoutSection
          title={
            checkout.toDelivery
              ? "Entrega"
              : checkout.toGo
                ? "Retirada"
                : "Na loja"
          }
        >
          <div className="status-card-body">
            {checkout.toDelivery && deliveryAddress && (
              <>
                <p className="status-card-strong">
                  {deliveryAddress.addressStreet},{" "}
                  {deliveryAddress.addressNumber}
                  {deliveryAddress.complement
                    ? ", " + deliveryAddress.complement
                    : ""}
                </p>
                <p className="status-card-text">
                  {deliveryAddress.neighborhood}, {deliveryAddress.city}/
                  {deliveryAddress.state} - CEP {deliveryAddress.postalCode}
                </p>
              </>
            )}
            {checkout.toGo && (
              <p className="status-card-strong">
                Retirar na loja
                {shop && shop.addressStreet && isSameShop && (
                  <span className="status-card-text d-block">
                    {shop.addressStreet}, {shop.addressNumber}
                    {shop.neighborhood ? ` - ${shop.neighborhood}` : ""}
                  </span>
                )}
              </p>
            )}
            {cache.isInLoco && (
              <p className="status-card-strong">
                O pedido será entregue em: {inLocoText}
              </p>
            )}
            <p className="status-card-text status-card-customer">
              {checkout.name}
              {checkout.telephone && ` · ${phoneDisplay(checkout.telephone)}`}
              {checkout.identification &&
                ` · ${documentLabelDisplay(
                  checkout.identification,
                )} ${documentDisplay(checkout.identification)}`}
            </p>
          </div>
        </CheckoutSection>
      )}

      {isSameShop && (whatsappDigits || shop.phone) && (
        <CheckoutSection title="Precisa de ajuda?">
          <div className="status-card-body">
            <p className="status-card-text">
              Para mudar ou cancelar o pedido, fale com a loja.
            </p>
            <div className="status-contact">
              {whatsappDigits && (
                <a
                  className="btn btn-whatsapp status-card-button"
                  href={`${WHATSAPP_CHAT_URL}/55${whatsappDigits}?text=${encodeURI(
                    message,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon className="me-2" fontSize="small" />
                  WhatsApp
                </a>
              )}
              {shop.phone && (
                <a
                  className="btn btn-outline-shop status-card-button"
                  href={`tel:${shop.phone.replace(/[^\d+]/g, "")}`}
                >
                  <PhoneOutlinedIcon className="me-2" fontSize="small" />
                  Ligar
                </a>
              )}
            </div>
          </div>
        </CheckoutSection>
      )}
    </>
  );
}

function OrderCosts({ checkout, costs }) {
  return (
    <div className="checkout-costs order-costs">
      {costs.showSubtotal && (
        <CostLine label="Subtotal" value={toPriceDisplay(costs.subtotal)} />
      )}
      {checkout.toDelivery && (
        <CostLine label="Entrega" value={toPriceDisplay(costs.delivery)} />
      )}
      {checkout.coupon && (
        <CostLine
          label="Cupom"
          value={`-${toPriceDisplay(costs.discount)}`}
          positive
        />
      )}
      {costs.hasTakeoutDiscount && (
        <CostLine
          label="Desconto para retirar"
          value={`-${toPriceDisplay(costs.takeoutDiscount)}`}
          positive
        />
      )}
      {costs.fidelityDiscount && (
        <CostLine
          label="Desconto fidelidade"
          value={`-${toPriceDisplay(costs.fidelityDiscount)}`}
          positive
        />
      )}
      <p className="d-flex justify-content-between fw-bold checkout-costs__total">
        <span>Total</span>
        <span>{toPriceDisplay(costs.total)}</span>
      </p>
      {checkout.payment && (
        <CostLine
          label={checkout.payment}
          value={toPriceDisplay(costs.paymentValue)}
        />
      )}
      {costs.paymentIsCash && (
        <CostLine label="Troco" value={toPriceDisplay(costs.paymentDiff)} />
      )}
    </div>
  );
}

function CostLine({ label, value, positive = false }) {
  return (
    <p className="d-flex justify-content-between text-muted">
      <span>{label}</span>
      <span className={positive ? "text-success" : ""}>{value}</span>
    </p>
  );
}
